"use client"

import { Apple, Mail } from "lucide-react"
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"
import { Field, FieldDescription, FieldError, FieldGroup } from "@/components/ui/field"
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp"
import { useMemo, useState } from "react"

import { Button } from "@/components/ui/button"
import {
    AuthUserButton,
    type AuthUserButtonColor,
} from "@/app/components/common/AuthUserButton"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { observer } from "mobx-react-lite"
import { toastUtils } from "@/utils/toast-utils"
import { useRouter } from "next/navigation"
import { useStore } from "@/app/components/common/store.provider"
import { NotRegisteredModal } from "./NotRegisteredModal"

const OTP_LENGTH = 6

type LoginModalProps = {
    open: boolean
    onOpenChange: (open: boolean) => void
    hasTrigger?: boolean
    /** Color theme for the auth trigger button */
    triggerColor?: AuthUserButtonColor
    onSuccess?: () => void
    shouldCloseOnSuccess?: boolean
}

const LoginModal = observer(({
    open,
    onOpenChange,
    hasTrigger = true,
    triggerColor = "brand",
    onSuccess,
    shouldCloseOnSuccess = false,
}: LoginModalProps) => {
    const { authStore } = useStore()

    const router = useRouter()

    const [email, setEmail] = useState("")
    const [otp, setOtp] = useState("")
    const [showNotRegisteredModal, setShowNotRegisteredModal] = useState(false)
    const [isRegistering, setIsRegistering] = useState(false)
    const [unregisteredEmail, setUnregisteredEmail] = useState("")

    const isOtpStep = authStore.otpSent
    const isRequesting = authStore.isLoading
    const isVerifying = authStore.otpVerifying
    const isLoggedIn = authStore.isAuthenticated

    const canRequestOtp = useMemo(() => {
        return email.trim().length > 0 && !isRequesting
    }, [email, isRequesting])

    const sanitizedOtp = useMemo(() => otp.replace(/\D/g, "").slice(0, OTP_LENGTH), [otp])
    const canVerifyOtp = sanitizedOtp.length === OTP_LENGTH && !isVerifying

    const resolvePostLoginRoute = () => {
        const isStaff =
            authStore.user?.role === "staff" ||
            ["superadmin", "admin", "manager", "marketer", "finance", "non_essential", "creator"].includes(
                authStore.role ?? ""
            ) ||
            ["superadmin", "admin", "manager", "marketer", "finance", "non_essential", "creator"].includes(
                authStore.user?.staff?.role ?? ""
            )
        return isStaff ? "/business/appointments" : "/customer/dashboard"
    }

    const resetLocalState = () => {
        setOtp("")
        setEmail("")
        setUnregisteredEmail("")
        setShowNotRegisteredModal(false)
        setIsRegistering(false)
        authStore.resetOTPFlow()
    }

    const handleOpenChange = (nextOpen: boolean) => {
        if (isLoggedIn) {
            router.push(resolvePostLoginRoute())
            return
        }
        onOpenChange(nextOpen)
        if (!nextOpen) {
            resetLocalState()
        }
    }

    const handleRequestOtp = async (event: React.SubmitEvent<HTMLFormElement>) => {
        event.preventDefault()
        const normalizedEmail = email.trim().toLowerCase()
        if (!normalizedEmail) return
        const res = await authStore.requestOTP(normalizedEmail)
        if (res.notRegistered) {
            setUnregisteredEmail(normalizedEmail)
            setShowNotRegisteredModal(true)
            return
        }
        const code = res.token
        if (code) {
            setOtp(code)
        }
    }

    const handleConfirmRegister = async () => {
        const targetEmail = unregisteredEmail || email.trim().toLowerCase()
        if (!targetEmail) return
        setIsRegistering(true)
        try {
            const res = await authStore.requestOTP(targetEmail, { autoRegister: true })
            if (res.success) {
                setShowNotRegisteredModal(false)
                const code = res.token
                if (code) {
                    setOtp(code)
                }
            }
        } finally {
            setIsRegistering(false)
        }
    }

    const handleCancelRegister = () => {
        setShowNotRegisteredModal(false)
    }

    const handleVerifyOtp = async (event: React.SubmitEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (sanitizedOtp.length !== OTP_LENGTH) return

        const result = await authStore.verifyOTP(sanitizedOtp)
        if (result.success) {
            onOpenChange(false)
            resetLocalState()
            if (!shouldCloseOnSuccess) {
                router.push(resolvePostLoginRoute())
            }
            onSuccess?.()
            resetLocalState()
        }
    }

    const handleResend = async () => {
        const targetEmail = authStore.pendingEmail ?? email.trim().toLowerCase()
        if (!targetEmail) return
        const res = await authStore.requestOTP(targetEmail)
        const code = res.token
        if (code) {
            setOtp(code)
        }
    }

    const handleGoogleLogin = async () => {
        const result = await authStore.loginWithGoogle()
        if (result.success) {
            onOpenChange(false)
            resetLocalState()
            if (!shouldCloseOnSuccess) {
                router.push(resolvePostLoginRoute())
            }
            onSuccess?.()
        }
    }

    const handleProviderLogin = (provider: "Google" | "Apple") => {
        if (provider === "Google") {
            void handleGoogleLogin()
            return
        }
        toastUtils.info(`${provider} Sign In`, `${provider} sign-in will be wired shortly.`)
    }

    return (
        <>
            <Dialog open={open} onOpenChange={handleOpenChange}>
                {hasTrigger ? (
                    <DialogTrigger asChild>
                        <AuthUserButton
                            color={triggerColor}
                            showLabel="lg"
                            className="w-auto min-h-0 border-0 bg-transparent px-2 hover:bg-transparent"
                        />
                    </DialogTrigger>
                ) : null}

                <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-sm">
                    {!isOtpStep ? (
                        <form onSubmit={handleRequestOtp} className="space-y-6">
                            <DialogHeader>
                                <DialogTitle>Welcome back</DialogTitle>
                                <DialogDescription>
                                    Create an account or log in to book and manage your appointments.
                                </DialogDescription>
                            </DialogHeader>

                            <FieldGroup>
                                <Field>
                                    <Label htmlFor="login-email">Email address</Label>
                                    <Input
                                        id="login-email"
                                        name="email"
                                        type="email"
                                        placeholder="you@example.com"
                                        value={email}
                                        onChange={(event) => setEmail(event.target.value)}
                                        autoComplete="email"
                                        required
                                    />
                                </Field>
                                {authStore.error && <FieldError>{authStore.error}</FieldError>}
                            </FieldGroup>

                            <Button className="w-full" type="submit" disabled={!canRequestOtp}>
                                <Mail className="h-4 w-4" />
                                {isRequesting ? "Sending code..." : "Continue with Email"}
                            </Button>

                            <div className="flex items-center my-4 text-sm">
                                <span className="flex-1 border-t border-muted" />
                                <span className="px-3 text-muted-foreground font-normal text-xs">OR</span>
                                <span className="flex-1 border-t border-muted" />
                            </div>

                            <div className="space-y-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full"
                                    disabled={isRequesting}
                                    onClick={() => handleProviderLogin("Google")}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                        <path
                                            d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                                            fill="currentColor"
                                        />
                                    </svg>
                                    {isRequesting ? "Signing in…" : "Sign in with Google"}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => handleProviderLogin("Apple")}
                                >
                                    <Apple className="h-4 w-4" />
                                    Sign in with Apple
                                </Button>
                            </div>

                            <DialogFooter>
                                {/* <DialogClose asChild>
                                <Button variant="ghost" type="button">
                                    Cancel
                                </Button>
                            </DialogClose> */}
                            </DialogFooter>
                        </form>
                    ) : (
                        <form onSubmit={handleVerifyOtp} className="space-y-6">
                            <DialogHeader>
                                <DialogTitle>Enter verification code</DialogTitle>
                                <DialogDescription>
                                    We sent a {OTP_LENGTH}-digit code to <span className="font-medium">{authStore.pendingEmail}</span>.
                                </DialogDescription>
                            </DialogHeader>

                            <FieldGroup>
                                <Field>
                                    <Label>One-time code</Label>
                                    <InputOTP
                                        value={sanitizedOtp}
                                        onChange={(value) => setOtp(value.replace(/\D/g, "").slice(0, OTP_LENGTH))}
                                        maxLength={OTP_LENGTH}
                                        className="justify-start"
                                    >
                                        <InputOTPGroup>
                                            {Array.from({ length: OTP_LENGTH }).map((_, index) => (
                                                <InputOTPSlot key={index} index={index} />
                                            ))}
                                        </InputOTPGroup>
                                    </InputOTP>
                                    <FieldDescription>Use the code from your inbox to continue.</FieldDescription>
                                </Field>
                                {authStore.error && <FieldError>{authStore.error}</FieldError>}
                            </FieldGroup>

                            <div className="flex items-center justify-between text-sm">
                                <button
                                    type="button"
                                    onClick={handleResend}
                                    className="text-primary hover:underline disabled:opacity-60"
                                    disabled={isRequesting}
                                >
                                    {isRequesting ? "Resending..." : "Resend code"}
                                </button>
                                <button
                                    type="button"
                                    className="text-muted-foreground hover:underline"
                                    onClick={() => {
                                        setOtp("")
                                        authStore.resetOTPFlow()
                                    }}
                                >
                                    Change email
                                </button>
                            </div>

                            <DialogFooter>
                                <DialogClose asChild>
                                    <Button variant="ghost" type="button">
                                        Cancel
                                    </Button>
                                </DialogClose>
                                <Button type="submit" disabled={!canVerifyOtp}>
                                    {isVerifying ? "Verifying..." : "Verify code"}
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
            <NotRegisteredModal
                open={showNotRegisteredModal}
                email={unregisteredEmail || email}
                onOpenChange={setShowNotRegisteredModal}
                onCancel={handleCancelRegister}
                onRegister={handleConfirmRegister}
                isRegistering={isRegistering}
            />
        </>
    )
})

export default LoginModal